import React, { useEffect, useRef } from 'react';
import { LopButton } from '../../components/LopButton/LopButton';
import { t } from '../../i18n/t';
import '../FundForm/FeeChangeConfirmDialog.css';

export interface UnlockUserDialogProps {
  email: string;
  /** Resolves when the unlock request settled; the dialog closes itself via onClose. */
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
}

// Confirmation for POST /api/users/{id}/unlock. Escape / backdrop / "ביטול" close it without any request.
// Reuses the shared confirm-dialog look (same classes as FeeChangeConfirmDialog).
export function UnlockUserDialog({ email, onConfirm, onClose }: UnlockUserDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fee-change-confirm-dialog__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        id="unlock-user-confirm"
        className="fee-change-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('users.lockout.confirmTitle')}
      >
        <div className="fee-change-confirm-dialog__header">
          <h2 className="fee-change-confirm-dialog__title">{t('users.lockout.confirmTitle')}</h2>
        </div>
        <div className="fee-change-confirm-dialog__body">
          <p>{t('users.lockout.confirmText', { email })}</p>
        </div>
        <div className="fee-change-confirm-dialog__actions">
          <LopButton
            id="unlock-user-confirm-ok"
            variant="primary"
            onClick={async () => {
              await onConfirm();
            }}
          >
            {t('users.lockout.confirmProceed')}
          </LopButton>
          <LopButton id="unlock-user-confirm-cancel" ref={cancelRef} variant="secondary" onClick={onClose}>
            {t('users.lockout.confirmCancel')}
          </LopButton>
        </div>
      </div>
    </div>
  );
}
